import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PageHeader from "../../shared/components/PageHeader";
import HeaderTermSelect, {
  headerSelectSx,
} from "../../shared/components/HeaderTermSelect";

const gradeLabel = (level) => (level === "all" ? "All grades" : level);

// Quick Find field: the in-progress text is local (a form draft kept off the
// URL); it is applied on submit/clear, when v1 writes the query param. Keyed by
// the applied query in StudentsHeader so an external reset reseeds it.
function StudentsSearchField({ query, onSearch }) {
  const [value, setValue] = useState(query ?? "");

  const submit = (e) => {
    e.preventDefault();
    onSearch(value.trim());
  };

  const clear = () => {
    setValue("");
    onSearch("");
  };

  return (
    <form onSubmit={submit}>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Quick Find"
        variant="standard"
        size="small"
        sx={{ width: { xs: "100%", md: 220 } }}
        slotProps={{
          input: {
            "aria-label": "Quick Find",
            endAdornment: (
              <InputAdornment position="end">
                {value ? (
                  <IconButton
                    aria-label="Clear search"
                    onClick={clear}
                    edge="end"
                  >
                    <ClearRoundedIcon />
                  </IconButton>
                ) : (
                  <IconButton aria-label="Search" type="submit" edge="end">
                    <SearchRoundedIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
    </form>
  );
}

// Page-header definition for the legacy v1 Students page. The page has no tabs;
// its controls - Quick Find, the term picker, the grade-level menu, the
// show-all/my-students toggle, and the count - all move into the right-aligned
// actions slot here. Data + callbacks cross the v1/v2 bridge; v1 keeps owning the
// term/grade/query params and the student fetch they drive.
export default function StudentsHeader({
  query,
  onSearch,
  terms = [],
  selectedTermId,
  onTermChange,
  gradeLevels = [],
  gradeLevel,
  onGradeLevelChange,
  manageAllPermission,
  onlyMyStudents,
  myStudentsCount,
  totalStudents,
  allStudentsCount,
  showShowAllToggle,
  onToggleShowAll,
}) {
  // Node title: the page carries a grade-level menu beside the term picker, so it
  // composes the shared term control (HeaderTermSelect) itself rather than using
  // PageHeader's `termSelector` prop, and matches the grade menu to it with the
  // same shared styling.
  return (
    <PageHeader
      title={
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
        >
          <Typography
            sx={{ fontSize: 24, lineHeight: 1.2, whiteSpace: "nowrap" }}
          >
            Students
          </Typography>

          <HeaderTermSelect
            terms={terms}
            value={selectedTermId}
            onChange={onTermChange}
          />

          <Select
            size="small"
            value={gradeLevel ?? "all"}
            onChange={(e) => onGradeLevelChange(e.target.value)}
            inputProps={{ "aria-label": "Grade level" }}
            renderValue={(v) => gradeLabel(v)}
            sx={headerSelectSx}
          >
            {gradeLevels.map((level) => (
              <MenuItem key={level} value={level}>
                {gradeLabel(level)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      }
      actions={
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "flex-end" }}
        >
          {showShowAllToggle && (
            <Button
              onClick={onToggleShowAll}
              size="small"
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                textTransform: "none",
              }}
            >
              {onlyMyStudents ? "Show all" : "Show my students"}
            </Button>
          )}

          <Box sx={{ whiteSpace: "nowrap", flexShrink: 0, fontSize: 14 }}>
            {manageAllPermission && (
              <>
                <Typography component="span" sx={{ fontWeight: 500 }}>
                  {onlyMyStudents ? myStudentsCount : totalStudents}
                </Typography>{" "}
                of{" "}
              </>
            )}
            <Typography component="span" sx={{ fontWeight: 500 }}>
              {allStudentsCount}
            </Typography>{" "}
            students
          </Box>

          <StudentsSearchField key={query} query={query} onSearch={onSearch} />
        </Stack>
      }
    />
  );
}
